import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RejectWithMessage, TableDataResponse } from 'features/types';
import { getErrorMessage } from 'utils/error';
import { RootState } from 'app/root-reducer';
import { Paging } from 'api/types';
import { GetJobRespondsByJobSeekerTableFilters, JobRespondsByJobSeekerTable, JobResponsesByJobSeekerState } from './types';
import { jobResponsesAPI } from 'api';

export const getJobResponsesTable = createAsyncThunk<TableDataResponse<JobRespondsByJobSeekerTable>, boolean | undefined, RejectWithMessage>(
  'job-responses-by-job-seeker/get-job-responses-table',
  async (forLead, { rejectWithValue, getState }) => {
    try {
      const state: RootState = getState();
      const requestData = state.jobResponsesByJobSeeker.requestData;
      if (forLead) {
        return jobResponsesAPI.getJobSeekerJobResponsesForLeadTable(requestData);
      }
      return jobResponsesAPI.getJobSeekerJobResponsesTable(requestData)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

const initialState: JobResponsesByJobSeekerState = {
  status: 'idle',
  error: null,
  requestData: {
    paging: {
      page: 1,
      pageSize: 20,
    },
    filters: {},
  },
  data: {
    tableDataIsLoading: false,
    tableData: {
      rows: [],
      totalCount: 0,
      noResult: false,
    },
  }
};

const jobResponsesByJobSeekerSlice = createSlice({
  name: 'job-responses-by-job-seeker',
  initialState,
  reducers: {
    setPage: (state, action: PayloadAction<Paging>) => {
      state.requestData.paging.page = action.payload.page;
      state.requestData.paging.pageSize = action.payload.pageSize;
    },
    resetState: () => initialState,
    setFilters: (state, action: PayloadAction<GetJobRespondsByJobSeekerTableFilters>) => {
      state.requestData.filters = { ...action.payload };
      state.requestData.paging = initialState.requestData.paging;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getJobResponsesTable.pending, (state) => {
      state.data.tableDataIsLoading = true;
    });
    builder.addCase(getJobResponsesTable.fulfilled, (state, action: PayloadAction<TableDataResponse<JobRespondsByJobSeekerTable>>) => {
      state.data.tableDataIsLoading = false;
      state.data.tableData = action.payload;
      state.data.tableData.noResult = state.data.tableData.rows.length === 0;
    });
    builder.addCase(getJobResponsesTable.rejected, (state) => {
      state.data.tableDataIsLoading = false;
    });
  }
});

export const { setPage, resetState, setFilters } = jobResponsesByJobSeekerSlice.actions;

export const jobResponsesByJobSeekerReducer = jobResponsesByJobSeekerSlice.reducer;
